import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, Eye, Ban, CheckCircle } from 'lucide-react';
import { trainerService } from '../../services/trainer-service';
import type { TrainerProfileAdminDto, TrainerAdminQueryDto } from '../../types/admin-api';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

const columnHelper = createColumnHelper<TrainerProfileAdminDto>();

export function TrainerList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [city, setCity] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    trainerId: string;
    trainerName: string;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    trainerId: '',
    trainerName: '',
    action: 'enable',
  });
  const [reason, setReason] = useState('');

  const query: TrainerAdminQueryDto = {
    page,
    pageSize,
    search: search || undefined,
    status: status === 'all' ? undefined : (status as 'active' | 'disabled'),
    city: city || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['trainers', query],
    queryFn: () => trainerService.getAll(query),
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ id, isEnabled, reason }: { id: string; isEnabled: boolean; reason?: string }) =>
      trainerService.setStatus(id, { isEnabled, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainers'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setConfirmDialog({ isOpen: false, trainerId: '', trainerName: '', action: 'enable' });
      setReason('');
    },
  });

  const handleStatusChange = (trainer: TrainerProfileAdminDto, action: 'enable' | 'disable') => {
    setConfirmDialog({
      isOpen: true,
      trainerId: trainer.id,
      trainerName: trainer.displayName,
      action,
    });
  };

  const handleConfirmStatusChange = () => {
    const isEnabled = confirmDialog.action === 'enable';
    setStatusMutation.mutate({
      id: confirmDialog.trainerId,
      isEnabled,
      reason: isEnabled ? undefined : reason,
    });
  };

  const columns = [
    columnHelper.accessor('displayName', {
      header: 'Trainer Name',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{info.getValue()}</div>
          {info.row.original.companyName && (
            <div className="text-sm text-ink-muted">{info.row.original.companyName}</div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('phoneNumber', {
      header: 'Phone',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('city', {
      header: 'City',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('trainingCount', {
      header: 'Trainings',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('hasTPay', {
      header: 'TPay',
      cell: (info) => (
        <span className={info.getValue() ? 'text-brand-confirm' : 'text-ink-subtle'}>
          {info.getValue() ? 'Yes' : 'No'}
        </span>
      ),
    }),
    columnHelper.accessor('isEnabled', {
      header: 'Status',
      cell: (info) => <StatusBadge isEnabled={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/trainers/${info.row.original.id}`)}
            className="p-1 text-brand-primary-light hover:bg-white/10 rounded"
            title="View Details"
          >
            <Eye size={18} />
          </button>
          {info.row.original.isEnabled ? (
            <button
              onClick={() => handleStatusChange(info.row.original, 'disable')}
              className="p-1 text-brand-warm hover:bg-brand-warm/10 rounded"
              title="Disable"
            >
              <Ban size={18} />
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange(info.row.original, 'enable')}
              className="p-1 text-brand-confirm hover:bg-brand-confirm/10 rounded"
              title="Enable"
            >
              <CheckCircle size={18} />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading trainers: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink-primary mb-4">All Trainers</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-subtle"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by name, email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="disabled">Disabled Only</option>
          </select>

          {/* City Filter */}
          <input
            type="text"
            placeholder="Filter by city"
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />

          {/* Clear Filters */}
          {(search || status !== 'all' || city) && (
            <button
              onClick={() => {
                setSearch('');
                setStatus('all');
                setCity('');
                setPage(1);
              }}
              className="px-4 py-2 text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-ink-muted">Loading trainers...</div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-ink-secondary uppercase tracking-wider"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-white/10">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-white/5">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-ink-secondary">
                  Showing page {data.pagination.page} of {data.pagination.totalPages} (
                  {data.pagination.totalItems} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-ink-muted">No trainers found</div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => {
          setConfirmDialog({ isOpen: false, trainerId: '', trainerName: '', action: 'enable' });
          setReason('');
        }}
        onConfirm={handleConfirmStatusChange}
        title={confirmDialog.action === 'enable' ? 'Enable Trainer' : 'Disable Trainer'}
        message={`Are you sure you want to ${confirmDialog.action} "${confirmDialog.trainerName}"?`}
        confirmText={confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
        confirmVariant={confirmDialog.action === 'disable' ? 'danger' : 'primary'}
        isLoading={setStatusMutation.isPending}
      >
        {confirmDialog.action === 'disable' && (
          <div className="mt-4">
            <label htmlFor="reason" className="block text-sm font-medium text-white/70 mb-2">
              Reason for disabling (optional)
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={3}
              placeholder="Enter reason..."
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
